import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Settings, Lock, Save, ClipboardList, CheckCircle, X } from 'lucide-react';

const ProfileTab = ({
  profileForm = {},
  setProfileForm = () => {},
  savingProfile = false,
  handleSaveProfile = () => {},
  attendance = { students: [], attendance_records: [], summary: {} },
  loadingAttendance = false
}) => {
  // Ensure profileForm has all required fields with defaults
  const form = {
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    ...(profileForm || {})
  };
  
  const updateForm = (updates) => {
    if (setProfileForm) {
      setProfileForm({ ...form, ...updates });
    }
  };

  const safeAttendance = attendance || { students: [], attendance_records: [], summary: {} };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Contact Information */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-cyan-400" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white/70">First Name</Label>
              <Input
                value={form.first_name || ''}
                onChange={(e) => updateForm({ first_name: e.target.value })}
                className="bg-[#1a1a1a] border-[#333] text-white"
                data-testid="profile-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Last Name</Label>
              <Input
                value={form.last_name || ''}
                onChange={(e) => updateForm({ last_name: e.target.value })}
                className="bg-[#1a1a1a] border-[#333] text-white"
                data-testid="profile-last-name"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-white/70">Email Address</Label>
            <Input
              type="email"
              value={form.email || ''}
              onChange={(e) => updateForm({ email: e.target.value })}
              className="bg-[#1a1a1a] border-[#333] text-white"
              data-testid="profile-email"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-white/70">Phone Number</Label>
            <Input
              type="tel"
              value={form.phone || ''}
              onChange={(e) => updateForm({ phone: e.target.value })}
              className="bg-[#1a1a1a] border-[#333] text-white"
              placeholder="07700 900000"
              data-testid="profile-phone"
            />
          </div>
          
          <Button 
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className="w-full bg-[#FF007F] hover:bg-[#D6006A]"
            data-testid="save-profile-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingProfile ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-400" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white/70">Current Password</Label>
            <Input
              type="password"
              value={form.current_password || ''}
              onChange={(e) => updateForm({ current_password: e.target.value })}
              className="bg-[#1a1a1a] border-[#333] text-white"
              placeholder="••••••••"
              data-testid="current-password"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-white/70">New Password</Label>
            <Input
              type="password"
              value={form.new_password || ''}
              onChange={(e) => updateForm({ new_password: e.target.value })}
              className="bg-[#1a1a1a] border-[#333] text-white"
              placeholder="••••••••"
              data-testid="new-password"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-white/70">Confirm New Password</Label>
            <Input
              type="password"
              value={form.confirm_password || ''}
              onChange={(e) => updateForm({ confirm_password: e.target.value })}
              className="bg-[#1a1a1a] border-[#333] text-white"
              placeholder="••••••••"
              data-testid="confirm-password"
            />
          </div>
          
          <Button 
            onClick={handleSaveProfile}
            disabled={savingProfile || !form.current_password || !form.new_password}
            className="w-full bg-amber-500 hover:bg-amber-600"
            data-testid="change-password-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            Update Password
          </Button>
        </CardContent>
      </Card>

      {/* Attendance Summary */}
      <Card className="bg-[#111] border-[#333] lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-emerald-400" />
            Attendance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingAttendance ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : safeAttendance.students?.length > 0 ? (
            <div className="space-y-4">
              {safeAttendance.students.map((student, idx) => {
                const studentRecords = safeAttendance.attendance_records?.filter(r => 
                  r.student_name?.toLowerCase() === student.name?.toLowerCase()
                ) || [];
                const present = studentRecords.filter(r => r.present === true).length;
                const total = studentRecords.length;
                const percentage = total > 0 ? Math.round((present / total) * 100) : 0;
                
                return (
                  <div key={idx} className="p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{student.name}</span>
                      <Badge className={percentage >= 80 ? 'bg-emerald-500/20 text-emerald-400' : percentage >= 60 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}>
                        {percentage}% ({present}/{total})
                      </Badge>
                    </div>
                    <div className="w-full bg-[#333] rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${percentage >= 80 ? 'bg-emerald-500' : percentage >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-white/50">
              <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No attendance records yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileTab;
